import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { AUTH } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey, Envelope, ShieldCheck } from "@phosphor-icons/react";

const DEMO = [
  { label: "Super Admin", email: "superadmin@agency.hr", role: "Agency", testid: AUTH.demoAdmin },
  { label: "Company Admin", email: "companyadmin@acme.hr", role: "Acme", testid: AUTH.demoCompanyAdmin },
  { label: "HR", email: "hr@acme.hr", role: "Acme", testid: AUTH.demoHr },
  { label: "Manager", email: "priya.menon@acme.hr", role: "Acme", testid: AUTH.demoManager },
  { label: "Employee", email: "arjun.iyer@acme.hr", role: "Acme", testid: AUTH.demoEmployee },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      const to = location.state?.from?.pathname || "/app";
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user?.name?.split(" ")[0] || ""}`);
      navigate("/app", { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  const fill = (em) => { setEmail(em); setPassword("Admin@12345"); };

  const forgot = async () => {
    if (!email.trim()) { toast.error("Enter your work email above, then click Forgot password."); return; }
    try { await api.post("/auth/forgot-password", { email: email.trim() }); }
    catch (_) { /* ignore — never reveal whether the account exists */ }
    toast.success("If that email is registered, a password reset link is on its way.");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      {/* Left visual panel */}
      <div className="relative hidden md:block">
        <img
          alt="Corporate architecture"
          src="https://images.pexels.com/photos/7424584/pexels-photo-7424584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zenith-blue/85 via-zenith-blue/70 to-zenith-magenta/50 mix-blend-multiply" />
        <div className="absolute inset-0 bg-zinc-950/45" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-white rounded-sm grid place-items-center p-1.5 shadow-lg shadow-zenith-magenta/30">
              <img src="/zenith360-mark.png" alt="Zenith360 HR" className="max-w-full max-h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-2xl tracking-tight">Zenith360 HR</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 mt-0.5">Your complete HR Ecosystem</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.3em] text-zenith-green mb-3 flex items-center gap-2">
              <span className="inline-block w-8 h-0.5 bg-gradient-to-r from-zenith-teal via-zenith-magenta to-zenith-green"></span>
              HR Outsourcing Platform
            </div>
            <h1 className="font-display text-4xl lg:text-5xl leading-[1.05] font-bold">
              One ecosystem for every company you run <span className="italic bg-gradient-to-r from-zenith-teal via-white to-zenith-green bg-clip-text text-transparent">HR for</span>.
            </h1>
            <p className="mt-5 text-white/90 text-base leading-relaxed">
              Multi-tenant, India-first payroll and compliance ready — from geo-fenced attendance to project allocation and statutory returns.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
              {[
                { t: "Multi-tenant", accent: "border-zenith-teal/40 text-zenith-teal" },
                { t: "Geo + Selfie", accent: "border-zenith-magenta/40 text-zenith-magenta" },
                { t: "PF · ESIC · PT · TDS", accent: "border-zenith-green/40 text-zenith-green" },
              ].map((f) => (
                <div key={f.t} className={`border ${f.accent} bg-white/5 rounded-sm px-3 py-2 backdrop-blur-sm`}>
                  <ShieldCheck size={14} className="inline mr-1" /> <span className="text-white/95">{f.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="group" data-testid="login-brand-mark">
            <div className="flex items-center gap-2 mt-1">
              <div className="w-2 h-2 rounded-full bg-adb-green"></div>
              <div className="font-display text-base font-bold text-white tracking-tight">Zenith360 HR</div>
              <div className="text-[10px] italic text-white/50">· Your complete HR Ecosystem</div>
            </div>
            <div className="text-[10px] text-white/30 mt-2">© {new Date().getFullYear()} Zenith360 HR</div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-zinc-950">Welcome back.</h2>
            <p className="text-sm text-zinc-500 mt-2">Sign in with your email, employee ID or phone number.</p>
          </div>

          <form onSubmit={submit} data-testid={AUTH.loginForm} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email / Employee ID / Phone</Label>
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="email"
                  type="text"
                  autoComplete="username"
                  data-testid={AUTH.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com · ACM-101 · 9876543210"
                  className="pl-9 rounded-sm border-zinc-300 focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:border-emerald-700"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid={AUTH.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 rounded-sm border-zinc-300 focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:border-emerald-700"
                  required
                />
              </div>
            </div>

            {error && (
              <div data-testid={AUTH.errorAlert} className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid={AUTH.submitBtn}
              disabled={loading}
              className="w-full btn-zenith text-white rounded-sm h-11 transition-colors duration-200"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </Button>
          </form>

          <div className="mt-3 text-right">
            <button type="button" onClick={forgot} data-testid="forgot-password-btn"
              className="text-xs text-emerald-700 hover:underline">Forgot password?</button>
          </div>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Demo accounts (password: Admin@12345)</div>
            <div className="grid grid-cols-1 gap-1.5">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fill(d.email)}
                  data-testid={d.testid}
                  className="text-left text-sm border border-zinc-200 hover:border-emerald-500 rounded-sm px-3 py-2 flex items-center justify-between transition-colors duration-150 bg-white"
                >
                  <span>
                    <span className="font-medium text-zinc-900">{d.label}</span>
                    <span className="text-zinc-500"> · {d.email}</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-emerald-700">Fill</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
